import { useStoreState, useStoreActions } from '../../store';
import { Row, Col, Alert, Typography, Select, Switch } from 'antd';
import { useMemo, useEffect } from 'react';
import { useSimulationReport, useReportFilterParams } from '../../hooks/simulation/useSimulationReport';
import type {
  MarginSimulationReportData,
  SimulationDetail,
  SimulationSellRateMapping,
} from '../../store/simulation/types';
import { SimulationReportFilters } from './SimulationReportFilters';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { sumSellRateFields } from './utils';
import type { SellRateReportDatum } from '../../components/simulation/simulationReporting/SimulationSellRateTable';
import { SimulationSellRateZoneTable } from '../../components/simulation/simulationReporting/SimulationSellRateTable';
import { SimulationCard } from './SimulationCard';
import { ReportFilterParams } from './SimulationDetailTabs';
import { SimulationMarginReportHistoricalRow } from '../../components/simulation/simulationReporting/SimulationMarginReportHistoricalRow';
import { SimulationMarginReportMarginRow } from '../../components/simulation/simulationReporting/SimulationMarginReportMarginRow';
import { SimulationMarginReportMarginTotalsRow } from '../../components/simulation/simulationReporting/SimulationMarginReportTotalsRow';
const { Title } = Typography;

export interface SimulationDetailTabProps {
  simulation: SimulationDetail;
}
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});
const renderSellRateSurchargeModifier = (showingSurcharges: boolean, sellRate?: SimulationSellRateMapping) => {
  if (!showingSurcharges || !sellRate || !sellRate.carrierSurchargeModifierValue) {
    return;
  }
  const isDisount = ['flatDiscount', 'percentageDiscount'].includes(sellRate.carrierSurchargeModifierType ?? '');
  if (['percentageIncrease', 'percentageDiscount'].includes(sellRate.carrierSurchargeModifierType ?? '')) {
    return `Carrier surcharges ${isDisount ? 'discounted' : 'increased'} ${
      sellRate.carrierSurchargeModifierValue * 100
    }%`;
  } else {
    return `Carrier surcharges ${isDisount ? 'discounted' : 'increased'} ${currencyFormatter.format(
      sellRate.carrierSurchargeModifierValue,
    )}`;
  }
};
export const SimulationMargingReportTab = ({ simulation }: SimulationDetailTabProps) => {
  const simulationId = simulation.simulationId;
  const fetchSimulationSellRateMappings = useStoreActions(
    (actions) => actions.simulation.fetchSimulationSellRateMappings,
  );
  const simulationSellRateMappings = useStoreState((state) => state.simulation.simulationSellRateMappings);
  const sellRateMappings = simulationSellRateMappings[simulationId] ?? [];
  const isSellRate = simulation.simulationType === 'sell_rate_simulation';
  useEffect(() => {
    if (simulation && isSellRate) {
      fetchSimulationSellRateMappings(simulation.simulationId);
    }
  }, [simulation, fetchSimulationSellRateMappings, isSellRate]);

  const isComplete = simulation.simulationStatus === 'completed';
  const [query, setQuery] = useSearchParams();
  const baseParams = useReportFilterParams();
  const queryParams = useMemo(
    () => ({
      ...baseParams,
      sellRateId: query.get(ReportFilterParams.SELL_RATE_ID) || undefined,
      includeCarrierSurcharges: query.get(ReportFilterParams.INCLUDE_CARRIER_SURCHARGES)?.toLowerCase() !== 'false',
    }),
    [baseParams, query],
  );
  useEffect(() => {
    if (sellRateMappings.length > 0 && !queryParams.sellRateId) {
      query.set(ReportFilterParams.SELL_RATE_ID, sellRateMappings[0].carrierRateId);
      setQuery(query);
    }
  });
  // sell-rate-margin-report is not ported to v2, so this always reads v1 through the hook.
  const reportParams = useMemo(
    () => ({
      tntType: queryParams.tntType,
      carrier: queryParams.carrier,
      dayOfWeek: queryParams.dayOfWeek,
      tenantId: queryParams.tenantId,
      originSlug: queryParams.originSlug,
      sellCarrierRateId: queryParams.sellRateId,
      includeCarrierSurcharges: queryParams.includeCarrierSurcharges,
    }),
    [queryParams],
  );
  const { reportData: marginReportRaw, loading: reportLoading } = useSimulationReport(
    simulation,
    'sell-rate-margin-report',
    reportParams,
  );
  const reportData: MarginSimulationReportData = marginReportRaw;
  const loading = reportLoading || !reportData;
  const reportDataByZone: SellRateReportDatum[] = useMemo(
    () =>
      Object.values(
        reportData?.sellRateCarrierSelections?.reduce((prev: any, val: any) => {
          if (val.sellRateZoneId) {
            prev[val.sellRateZoneId] = {
              sellRateZoneId: val.sellRateZoneId,
              ...sumSellRateFields(val, prev[val.sellRateZoneId], queryParams.includeCarrierSurcharges),
            };
          }
          return prev;
        }, {}) ?? {},
      ).sort((a: any, b: any) => a.sellRateZoneId.localeCompare(b.sellRateZoneId)) as any,
    [reportData, queryParams.includeCarrierSurcharges],
  );
  const reportDataByMethod: SellRateReportDatum[] = useMemo(
    () =>
      Object.values(
        reportData?.sellRateCarrierSelections?.reduce((prev: any, val: any) => {
          if (val.carrierServiceMethodId) {
            prev[val.carrierServiceMethodId] = {
              carrierServiceMethodId: val.carrierServiceMethodId,
              ...sumSellRateFields(val, prev[val.carrierServiceMethodId], queryParams.includeCarrierSurcharges),
            };
          }
          return prev;
        }, {}) ?? {},
      ).sort((a: any, b: any) => a.carrierServiceMethodId.localeCompare(b.carrierServiceMethodId)) as any,
    [reportData, queryParams.includeCarrierSurcharges],
  );
  const selectedSellRateMapping = sellRateMappings.find((srm) => srm.carrierRateId === queryParams.sellRateId);
  useEffect(() => {
    if (!selectedSellRateMapping?.includeCarrierSurcharges) {
      query.set(ReportFilterParams.INCLUDE_CARRIER_SURCHARGES, 'false');
      setQuery(query);
    }
  }, [query, setQuery, selectedSellRateMapping]);
  return (
    <Row gutter={16}>
      {selectedSellRateMapping && (
        <Col style={{ marginBottom: 20 }} span={24}>
          <Alert
            showIcon={true}
            message={
              <p>
                Showing report for {selectedSellRateMapping.partnerRateName} rates.
                {queryParams.includeCarrierSurcharges
                  ? ' Margin and savings INCLUDING added carrier surcharges. '
                  : ' Margin and savings NOT INCLUDING carrier surcharges. '}
                {renderSellRateSurchargeModifier(queryParams.includeCarrierSurcharges, selectedSellRateMapping)}
              </p>
            }
          />
        </Col>
      )}
      <Col span={24}>
        {!isComplete && (
          <Alert
            type={'warning'}
            showIcon={true}
            description={'The Carrier Selection report will be available once the simulation has completed execution.'}
            message={'Simulation Incomplete'}
          />
        )}
        {isComplete && (
          <>
            {sellRateMappings?.length > 0 && (
              <>
                <SimulationCard
                  loading={loading}
                  subHeading={
                    'Select the sell rate to compare against historical and simulated label costs for this report'
                  }
                  heading={'Select Sell Rate'}
                  bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
                >
                  <Select
                    style={{ width: '100%' }}
                    value={query.get(ReportFilterParams.SELL_RATE_ID)}
                    onChange={(value) => {
                      if (!value || value === 'all') {
                        query.delete(ReportFilterParams.SELL_RATE_ID);
                      } else {
                        query.set(ReportFilterParams.SELL_RATE_ID, value);
                      }
                      setQuery(query);
                    }}
                    options={[
                      ...sellRateMappings.map((sr) => ({
                        label: `${sr.partnerRateName} (${sr.carrierId})`,
                        value: sr.carrierRateId,
                      })),
                    ]}
                  />
                </SimulationCard>
                {selectedSellRateMapping?.includeCarrierSurcharges && (
                  <SimulationCard
                    loading={loading}
                    subHeading={
                      'When selected we will apply the selected carrier surcharges to your sell rates for calculating margin and customer savings'
                    }
                    heading={'Include Carrier Surcharges'}
                    bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
                  >
                    <Switch
                      checked={query.get(ReportFilterParams.INCLUDE_CARRIER_SURCHARGES) !== 'false'}
                      onChange={(check) => {
                        query.set(ReportFilterParams.INCLUDE_CARRIER_SURCHARGES, String(check));
                        setQuery(query);
                      }}
                      unCheckedChildren="Not Including Carrier Surcharges"
                      checkedChildren="Including Carrier Surcharges"
                    />
                  </SimulationCard>
                )}
              </>
            )}
            <SimulationReportFilters simulation={simulation} />
            <SimulationMarginReportMarginRow loading={loading} reportData={reportData} />
            {reportData?.totalHistoricalCost > 0 && (
              <SimulationMarginReportHistoricalRow reportData={reportData} loading={loading} />
            )}
            <SimulationMarginReportMarginTotalsRow reportData={reportData} loading={loading} />
            <SimulationCard
              loading={false}
              heading={'Margin by Zone'}
              bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
            >
              <SimulationSellRateZoneTable reportAggregateField={'sellRateZoneId'} records={reportDataByZone} />
            </SimulationCard>
            <SimulationCard
              loading={false}
              heading={'Margin by Selected Method'}
              bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
            >
              <SimulationSellRateZoneTable
                reportAggregateField={'carrierServiceMethodId'}
                records={reportDataByMethod}
              />
            </SimulationCard>
          </>
        )}
      </Col>
    </Row>
  );
};
